import { useRef, useState, useEffect, useCallback } from 'react'
import { isAndroidDevice, isIosDevice } from '@fs/zion-frontend-friends'
import { domToDataUrl, domToJpeg } from 'modern-screenshot'

/**
 * Hook for managing shareable image generation
 *
 * This hook provides utilities for generating, downloading, and sharing
 * images from React components.
 *
 * @param {Object} options
 * @param {string} options.name - Name for the downloaded file (without extension)
 * @param {number} options.width - Width of the output image
 * @param {number} options.height - Height of the output image (optional)
 * @param {string} options.backgroundColor - Background color of the image
 * @param {boolean} options.generateOnLoad - Generate image immediately on mount (default: false)
 * @param {number} options.loadDelay - Delay in ms after fonts load before generating (default: 300, set to 0 to disable)
 * @param {boolean} options.useJpeg - Use JPEG format instead of PNG (default: false)
 * @param {number} options.scale - Scale factor for the image (default: 1, higher = better quality)
 * @param {number} options.quality - JPEG quality (0-1, only used if useJpeg is true)
 * @returns {{ imageDataUrl: string|null, generateImage: Function, downloadImage: Function, shareImage: Function, generatingImage: boolean, canShare: boolean, ref: React.RefObject<HTMLDivElement>, width: number, height: number, backgroundColor: string }} Image generation utilities and props
 *
 * @example
 * ```jsx
 * const shareableImage = useShareableImage({
 *   name: 'my-image',
 *   width: 1200,
 *   height: 630,
 *   generateOnLoad: true,
 * })
 *
 * return (
 *   <>
 *     <ShareableImage {...shareableImage.shareableImageProps}>
 *       <YourCustomContent />
 *     </ShareableImage>
 *     <Button onClick={shareableImage.downloadImage} disabled={shareableImage.generatingImage}>
 *       Download
 *     </Button>
 *   </>
 * )
 * ```
 */
export default function useShareableImage({
  name = 'image',
  width,
  height,
  backgroundColor,
  useJpeg = false,
  generateOnLoad = false,
  loadDelay = 300,
  quality = 0.9,
  ...screenshotOptions
}) {
  const shareableRef = useRef(null)
  const imageData = useRef(null)
  const [generatingImage, setGeneratingImage] = useState(false)
  const [imageDataUrl, setImageDataUrl] = useState(null)
  const fileType = useJpeg ? 'jpeg' : 'png'

  const generateImage = useCallback(
    /**
     * Generate an image from the shareableRef
     * @returns {Promise<string>} Data URL of the generated image
     */
    async () => {
      if (!shareableRef.current || generatingImage) return null
      if (imageData.current && imageData.current === imageDataUrl) return imageData.current

      setGeneratingImage(true)
      const element = shareableRef.current

      // Temporarily make the element visible for screenshot
      const originalClipPath = element.style.clipPath
      element.style.clipPath = 'none'

      let data = null
      const options = {
        debug: false,
        ...screenshotOptions,
      }

      try {
        if (useJpeg) {
          data = await domToJpeg(element, {
            quality,
            ...options,
          })
        } else {
          data = await domToDataUrl(element, options)
        }

        imageData.current = data
        setImageDataUrl(data)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error generating image:', error)
      } finally {
        // Restore original clip-path, or set to inset(100%) if it wasn't set
        element.style.clipPath = originalClipPath || 'inset(100%)'
      }

      setGeneratingImage(false)
      return data
    },
    [imageDataUrl, shareableRef, useJpeg, quality, screenshotOptions, generatingImage]
  )

  useEffect(() => {
    if (!imageData.current && generateOnLoad && !generatingImage) {
      // Add a delay to ensure fonts and layout are ready
      // Fonts may need time to load, especially web fonts
      const loadFontsAndGenerate = async () => {
        // Wait for fonts to load
        if (document.fonts) {
          await document.fonts.ready
        }
        // Additional delay to ensure layout is stable
        if (loadDelay > 0) {
          await new Promise((resolve) => {
            setTimeout(resolve, loadDelay)
          })
        }

        // Use requestIdleCallback to defer image generation until browser is idle
        // This prevents blocking the main thread and improves perceived performance
        if (globalThis.requestIdleCallback) {
          globalThis.requestIdleCallback(
            () => {
              generateImage()
            },
            { timeout: 2000 }
          ) // Fallback timeout of 2s
        } else {
          // Fallback for browsers that don't support requestIdleCallback
          setTimeout(() => {
            generateImage()
          }, 100)
        }
      }

      loadFontsAndGenerate()
    }
  }, [generateImage, generateOnLoad, generatingImage, loadDelay])

  const downloadImage = async () => {
    const link = document.createElement('a')
    link.download = `${name}.${fileType}`
    link.href = imageData.current
    link.click()
  }

  const shareImage = async () => {
    try {
      const res = await fetch(imageData.current)
      const blob = await res.blob()
      await navigator.share({
        files: [new File([blob], `${name}.${fileType}`, { type: `image/${fileType}` })],
      })
    } catch (e) {
      console.error(e)
    }
  }

  // Props object that can be spread directly onto ShareableImage component
  const shareableImageProps = {
    ref: shareableRef,
    ...(width && { width }),
    ...(height && { height }),
    ...(backgroundColor && { backgroundColor }),
    hide: !!imageDataUrl,
  }

  return {
    ...shareableImageProps,
    generatingImage,
    canShare: !!navigator.share && (isAndroidDevice() || isIosDevice()),
    downloadImage,
    shareImage,
    imageDataUrl,
    generateImage,
  }
}
